<template>
  <div
    class="tw-relative"
    :class="{
      'tw-flex tw-items-center tw-justify-between': !!label
    }"
  >
    <div v-if="label" class="tw-text-xs">
      {{ label }}
    </div>
    <button
      ref="colorPickerButton"
      class="tw-ml-4 tw-flex tw-h-7 tw-w-[130px] tw-cursor-pointer tw-items-center tw-overflow-hidden tw-rounded-md tw-border tw-text-2xs tw-leading-5 focus-within:tw-border-deep-orange-500 dark:tw-bg-neutral-900"
      :class="{
        'tw-cursor-not-allowed': disabled
      }"
      :disabled="disabled"
      @click="openColorPicker"
    >
      <input
        type="text"
        class="tw-h-7 tw-w-[80px] tw-flex-1 tw-border-none tw-font-mono tw-text-xs tw-text-black focus:tw-border-transparent focus:tw-ring-transparent dark:tw-text-white"
        :data-testid="label.replaceAll(' ', '-').toLowerCase()"
        :class="{
          'tw-cursor-pointer': !disabled,
          'tw-cursor-not-allowed': disabled
        }"
        :value="textInputValue"
        readonly
      />
      <div v-if="isTransparent" class="transparent-bg tw-h-7 tw-w-9" />
      <div v-else class="tw-h-7 tw-w-9 tw-border-l" :style="{ 'background-color': currentColor.style }" />
      <div
        v-if="isOpen"
        v-studio-click-outside="() => (isOpen = false)"
        class="tw-absolute tw-right-0 tw-top-8 tw-z-50 tw-w-auto tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-p-0 tw-shadow-lg"
      >
        <div class="tw-space-y-4 tw-p-4">
          <div class="tw-flex tw-items-center tw-justify-between">
            <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">Choose Color</h3>
          </div>

          <div class="tw-space-y-4">
            <div class="tw-flex tw-items-center tw-gap-2">
              <input
                :id="id"
                type="color"
                :required="required"
                :value="hexColor"
                class="tw-h-12 tw-w-12 tw-cursor-pointer tw-rounded-md tw-border tw-border-gray-300"
                @input="handleColorInput"
              />
              <input
                type="text"
                :value="hexColor"
                class="focus:tw-ring-primary tw-flex-1 tw-rounded-md tw-border tw-bg-white tw-px-2 tw-py-1.5 tw-font-mono tw-text-xs focus:tw-outline-none focus:tw-ring-1"
                placeholder="#000000"
                @input="handleTextInput"
              />
            </div>

            <div v-if="brandKitColors.length > 0" class="tw-mt-3 tw-border-t tw-pt-3">
              <div class="tw-mb-3 tw-flex tw-items-center tw-gap-2">
                <TablerBrush class="tw-h-4 tw-w-4 tw-text-gray-500" />
                <span class="tw-text-xs tw-font-medium tw-text-gray-500">Brand Kit Colors</span>
              </div>
              <div class="tw-flex tw-max-w-[240px] tw-flex-wrap tw-gap-2">
                <button
                  v-for="(color, index) in brandKitColors"
                  :key="`color-${index}`"
                  type="button"
                  class="hover:tw-border-primary focus:tw-ring-primary tw-h-8 tw-w-8 tw-rounded-md tw-border-2 tw-border-gray-300 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-1"
                  :style="{ backgroundColor: color }"
                  :aria-label="`Select brand kit color ${color}`"
                  @click="handleBrandKitColorClick(color)"
                />
              </div>
            </div>
            <BrandKitGoToSelectPrompt
              v-else
              class="tw-mt-3 tw-border-t tw-pt-3"
              @goToSelectBrandKit="handleGoToSelectBrandKit"
            />
          </div>
        </div>
      </div>
    </button>
  </div>
</template>

<script setup lang="ts">
import { BrandKit } from '@goldcast/api/backend';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';

import BrandKitGoToSelectPrompt from '@/studio/lookAndFeel/components/BrandKitGoToSelectPrompt.vue';
import TablerBrush from '@/studioComponents/Icons/tabler/TablerBrush.vue';
import { destructureRgbColor, rgbaToHex } from '@/utils/color';

const props = defineProps({
  id: { type: String, default: '' },
  label: { type: String, default: '' },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  initialColor: { type: String, default: 'rgba(0, 0, 0, 1)' }
});

const emit = defineEmits<{
  colorChange: [color: { red: number; green: number; blue: number; alpha: number; style: string; hex?: string }];
}>();

const store = useStore();
const isOpen = ref(false);

const currentColor = ref(
  destructureRgbColor(props.initialColor) || { red: 0, green: 0, blue: 0, alpha: 1, style: 'rgba(0, 0, 0, 1)' }
);

watch(
  () => props.initialColor,
  newColor => {
    const destructured = destructureRgbColor(newColor);
    if (destructured) {
      currentColor.value = destructured;
    }
  }
);

const selectedBrandKit = computed<BrandKit | null>(() => store.state.studioBrandKit.selectedBrandKit);

const brandKitColors = computed<string[]>(() => {
  if (!selectedBrandKit.value?.colors) {
    return [];
  }
  const colors = selectedBrandKit.value.colors;

  return [...colors.primary, ...colors.secondary, ...colors.neutral];
});

const textInputValue = computed<string>(() => {
  return rgbaToHex(currentColor.value.style, true);
});

const hexColor = computed<string>(() => {
  return rgbaToHex(currentColor.value.style, false);
});

const isTransparent = computed<boolean>(() => {
  return currentColor.value.alpha === 0;
});

function openColorPicker() {
  if (props.disabled) {
    return;
  }
  isOpen.value = true;
}

function emitColorChange(colorString: string) {
  const destructured = destructureRgbColor(colorString);
  if (destructured) {
    currentColor.value = destructured;
    emit('colorChange', {
      ...destructured,
      hex: rgbaToHex(destructured.style, true)
    });
  }
}

function handleColorInput(event: Event) {
  const target = event.target as HTMLInputElement;
  emitColorChange(target.value);
}

function handleTextInput(event: Event) {
  const target = event.target as HTMLInputElement;
  const value = target.value.trim();
  if (value.match(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)) {
    emitColorChange(value);
  }
}

function handleBrandKitColorClick(color: string) {
  emitColorChange(color);
}

function handleGoToSelectBrandKit() {
  isOpen.value = false;
  store.commit('branding/setSection', { sectionId: 'brandKit', moduleId: 'selectBrandKit' });
}
</script>

<style lang="scss" scoped>
.transparent-bg {
  background: url('../../../../tailwind/alpha-background.svg');
}
</style>
